import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface IdNameDto {
	id: string;
	name?: string | null;
}

export interface DomainListItemDto {
	id: string;
	name: string;
	pantheonName?: string | null;
	symbol?: string | null;
	imageAssetId?: string | null;
	godCount: number;
	updatedAt: string;
}

export interface DomainDto {
	id: string;
	worldId: string;
	pantheonId: string;
	pantheonName?: string | null;
	name: string;
	description?: string | null;
	symbol?: string | null;
	imageAssetId?: string | null;
	tenets?: string | null;
	foundryId?: string | null;
	gods: IdNameDto[];
	godIds: string[];
	createdAt: string;
	updatedAt: string;
}

export interface DomainsListResponse {
	domains: DomainListItemDto[];
	total: number;
}

export interface DomainGetResponse {
	domain: DomainDto;
}

export interface UpsertDomainRequest {
	pantheonId: string;
	name: string;
	description?: string | null;
	symbol?: string | null;
	imageAssetId?: string | null;
	tenets?: string | null;
	foundryId?: string | null;
}

export interface DomainListParams {
	search?: string;
	pantheonId?: string;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
	limit?: number;
	offset?: number;
}

@Injectable({ providedIn: 'root' })
export class DomainsService {
	constructor(private readonly http: HttpClient) {}

	list(worldId: string, args?: DomainListParams): Observable<DomainsListResponse> {
		let params = new HttpParams().set('format', 'list');
		if (args?.search) params = params.set('q', args.search);
		if (args?.pantheonId) params = params.set('pantheonId', args.pantheonId);
		if (args?.sortBy) params = params.set('sort', args.sortBy);
		if (args?.sortDirection) params = params.set('direction', args.sortDirection);
		if (args?.limit) params = params.set('limit', args.limit.toString());
		if (args?.offset) params = params.set('offset', args.offset.toString());
		return this.http.get<DomainsListResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/domains`, { params });
	}

	get(worldId: string, domainId: string): Observable<DomainGetResponse> {
		return this.http.get<DomainGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/domains/${domainId}`);
	}

	create(worldId: string, request: UpsertDomainRequest): Observable<DomainGetResponse> {
		return this.http.post<DomainGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/domains`, {
			PantheonId: request.pantheonId,
			Name: request.name,
			Description: request.description ?? null,
			Symbol: request.symbol ?? null,
			ImageAssetId: request.imageAssetId ?? null,
			Tenets: request.tenets ?? null,
			FoundryId: request.foundryId ?? null,
		});
	}

	update(worldId: string, domainId: string, request: UpsertDomainRequest): Observable<DomainGetResponse> {
		return this.http.put<DomainGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/domains/${domainId}`, {
			PantheonId: request.pantheonId,
			Name: request.name,
			Description: request.description ?? null,
			Symbol: request.symbol ?? null,
			ImageAssetId: request.imageAssetId ?? null,
			Tenets: request.tenets ?? null,
			FoundryId: request.foundryId ?? null,
		});
	}

	delete(worldId: string, domainId: string): Observable<void> {
		return this.http.delete<void>(`${environment.api.baseUrl}/v1/worlds/${worldId}/domains/${domainId}`);
	}
}
